import type { ReactNode } from 'react';
import { useState } from 'react';

import { errorMessage } from '@/shared/api/errors';
import { useFeatures } from '@/shared/session/currentUser';
import { Button } from '@/shared/ui/Button';
import { Callout } from '@/shared/ui/Callout';
import { Card } from '@/shared/ui/Card';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Spinner } from '@/shared/ui/Spinner';
import { formatDate } from '@/shared/utils/format';

import type { RetentionPreview, RetentionRun } from './types';
import { plural, summary } from './retentionText';
import { useRetention, useRunRetention } from './useAdmin';

const dateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'long' });
const shortDateFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short' });
const timeFormat = new Intl.DateTimeFormat('es-ES', { timeStyle: 'short' });

/**
 * A scheduled run that has not left a report for longer than this is late: one night
 * missed plus the slack of a slow purge. The page says so instead of leaving the reader
 * to compare dates.
 */
const LATE_AFTER_MS = 26 * 60 * 60 * 1000;

/**
 * What the system keeps, for how long, and why (Anexo II §4.a), told to the person who
 * administers the shop, not to a developer: where the deleted copy lived, that the
 * mailbox itself is never touched, what stays, and what tonight's run would do.
 *
 * The figures are the API's `--dry-run`: what would go if the run happened this minute.
 * A count that grows day after day, or a last run older than a night, is the sign the
 * nightly run is failing, and the page says it in words.
 */
export function DataTab() {
  const retention = useRetention();
  const run = useRunRetention();
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState<RetentionRun | null>(null);

  return (
    <div className="flex flex-col gap-4">
      {retention.isPending ? <Spinner variant="block" label="Cargando la retención" /> : null}
      {retention.isError ? (
        <ErrorMessage error={retention.error}>No se pudo cargar qué se borra</ErrorMessage>
      ) : null}
      {retention.data ? (
        <>
          <Explanation preview={retention.data} />

          <Card
            title="Qué se borraría hoy"
            aside={
              confirming ? (
                <span className="flex gap-2">
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={run.isPending}
                    onClick={() =>
                      run.mutate(undefined, {
                        onSuccess: (result) => {
                          setDone(result);
                          setConfirming(false);
                        },
                      })
                    }
                  >
                    {run.isPending ? 'Borrando…' : 'Sí, borrar ahora'}
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setConfirming(false)}>
                    No
                  </Button>
                </span>
              ) : (
                <Button size="sm" variant="secondary" onClick={() => setConfirming(true)}>
                  Ejecutar ahora
                </Button>
              )
            }
          >
            <Today preview={retention.data} />
            <p className="text-ink-3 text-[13px] leading-snug">
              {retention.data.scheduled
                ? 'No hace falta pulsar nada: «Ejecutar ahora» hace en este momento lo mismo que haría la pasada de la noche, y queda anotado en el registro de accesos.'
                : 'Lo que haga «Ejecutar ahora» queda anotado en el registro de accesos.'}
            </p>
          </Card>

          {run.isError ? (
            <p role="alert" className="text-danger text-[13.5px]">
              {errorMessage(run.error)}
            </p>
          ) : null}
          {done ? (
            // The run just made is the last run: one message, not the same figures twice.
            <Callout tone="ok" title="Hecho." role="status">
              {summary({
                bodies: done.bodies_purged,
                rawFiles: done.raw_files_deleted,
                attachments: done.attachments_deleted,
                accessLogRows: done.access_log_rows_deleted,
                guidelines: done.guidelines_deleted ?? 0,
              })}
              {done.raw_files_failed > 0
                ? ` ${done.raw_files_failed} ${plural(done.raw_files_failed, 'fichero no se pudo borrar', 'ficheros no se pudieron borrar')}: se reintenta mañana.`
                : ''}{' '}
              Queda anotado en el registro de accesos.
            </Callout>
          ) : (
            <LastRun preview={retention.data} />
          )}
        </>
      ) : null}
    </div>
  );
}

/**
 * Why the copy goes, where it goes from and what stays. Every number comes from the
 * API, so the text and the server never disagree about the days.
 */
function Explanation({ preview }: { preview: RetentionPreview }) {
  const { document_archive: documentArchive } = useFeatures();
  const days = preview.body_retention_days;
  return (
    <Card title="Qué se borra, de dónde y por qué">
      <div className="text-ink-2 flex flex-col gap-2 text-[14px] leading-relaxed">
        <p>
          Para clasificar un correo y enseñarlo en la bandeja, la aplicación guarda una copia de él
          en el servidor. Esa copia no se guarda para siempre:{' '}
          <strong className="text-ink">
            a los {days} días de haber llegado, un correo ya clasificado pierde su contenido
          </strong>
          . Lo pide el contrato de protección de datos (Anexo II): el texto de los correos se
          conserva solo el tiempo necesario, no indefinidamente.
        </p>
        <p>
          <strong className="text-ink">De dónde se borra:</strong> de la copia de la aplicación, en
          el servidor. El texto sale de la base de datos y el fichero original, del almacén de
          ficheros. <strong className="text-ink">Vuestro buzón de correo no se toca</strong>: el
          mensaje sigue en él tal como llegó.
        </p>
      </div>
      <div className="grid gap-4 text-[13.5px] leading-snug sm:grid-cols-2">
        <List title="Se borra" tone="danger">
          <li>El texto del correo, el que se lee en la bandeja.</li>
          <li>
            El fichero original completo (el mensaje tal cual llegó), del almacén de ficheros.
          </li>
          {documentArchive ? null : (
            <li>Los adjuntos, salvo los de un correo del que salió un presupuesto.</li>
          )}
          <li>
            Las anotaciones del registro de accesos con más de {preview.access_log_retention_days}{' '}
            días.
          </li>
          <li>
            Lo aprendido de las notas que ya no vale (lo de un solo correo, lo sustituido y lo
            desactivado), a los {days} días.
          </li>
        </List>
        <List title="Se queda" tone="ok">
          <li>El asunto, el remitente y la fecha.</li>
          <li>La categoría, quién la confirmó y qué se hizo con el correo.</li>
          <li>Las correcciones con sus notas, y lo aprendido que sigue vigente.</li>
          <li>Un correo pendiente de clasificar o con error: no se toca hasta que se resuelve.</li>
          {documentArchive ? (
            <li>Los adjuntos archivados: siguen su propio plazo, en «Documentos».</li>
          ) : null}
        </List>
      </div>
      <p className="text-ink-3 text-[13px] leading-snug">
        {preview.scheduled
          ? `Cuándo: cada noche a las ${preview.run_at}, sola.`
          : 'La pasada automática está apagada: solo borra «Ejecutar ahora».'}
      </p>
    </Card>
  );
}

function List({
  title,
  tone,
  children,
}: {
  title: string;
  tone: 'danger' | 'ok';
  children: ReactNode;
}) {
  const colour = tone === 'danger' ? 'text-danger' : 'text-ok';
  return (
    <div>
      <p className={`text-[12px] font-semibold tracking-[0.1em] uppercase ${colour}`}>{title}</p>
      <ul className="text-ink-2 mt-1.5 flex list-disc flex-col gap-1 pl-5">{children}</ul>
    </div>
  );
}

/** What tonight's run would take, in one sentence, with the cutoff date it applies. */
function Today({ preview }: { preview: RetentionPreview }) {
  const total =
    preview.bodies_to_purge +
    preview.raw_files_to_delete +
    preview.attachments_to_delete +
    preview.access_log_rows_to_delete +
    (preview.guidelines_to_delete ?? 0);
  if (total === 0) {
    return (
      <p className="text-ok text-[14px] font-semibold" role="status">
        Hoy no hay nada que borrar: ningún correo clasificado tiene más de{' '}
        {preview.body_retention_days} días.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-1 text-[14px]" role="status">
      <p className="text-ink font-semibold">
        {summary({
          bodies: preview.bodies_to_purge,
          rawFiles: preview.raw_files_to_delete,
          attachments: preview.attachments_to_delete,
          accessLogRows: preview.access_log_rows_to_delete,
          guidelines: preview.guidelines_to_delete ?? 0,
          future: preview.scheduled ? 'tonight' : 'now',
        })}
      </p>
      {preview.bodies_to_purge > 0 ? (
        <p className="text-ink-3 text-[13px] leading-snug">
          Son los correos recibidos antes del {formatDate(dateFormat, preview.cutoff)}.
        </p>
      ) : null}
    </div>
  );
}

/**
 * The last run, in one line, and a warning when a scheduled run has not happened when
 * it should have. `finished_at` is whatever the worker left in Redis, so nothing here
 * trusts its shape.
 */
function LastRun({ preview }: { preview: RetentionPreview }) {
  const last = preview.last_run;
  if (!last) {
    return (
      <Callout compact tone="info" title="Todavía no ha corrido ninguna pasada." role="status">
        {preview.scheduled
          ? `La primera será esta noche a las ${preview.run_at}; si mañana sigue sin aparecer aquí, no se está haciendo.`
          : null}
      </Callout>
    );
  }
  const finished = typeof last.finished_at === 'string' ? last.finished_at : null;
  const finishedMs = finished ? new Date(finished).getTime() : Number.NaN;
  const late =
    preview.scheduled && !Number.isNaN(finishedMs) && Date.now() - finishedMs > LATE_AFTER_MS;
  const when = finished
    ? `el ${formatDate(shortDateFormat, finished)} a las ${formatDate(timeFormat, finished)}`
    : 'sin terminar';
  const text = `${when}. ${summary({
    bodies: number(last.bodies_purged),
    rawFiles: number(last.raw_files_deleted),
    attachments: number(last.attachments_deleted),
    accessLogRows: number(last.access_log_rows_deleted),
    guidelines: number(last.guidelines_deleted),
  })}`;
  const failed = number(last.raw_files_failed);
  return (
    <div className="flex flex-col gap-2">
      <Callout compact tone="neutral" title="Última pasada:" role="status">
        {text}
        {failed > 0
          ? ` ${failed} ${plural(failed, 'fichero no se pudo borrar', 'ficheros no se pudieron borrar')}: se reintenta en la siguiente.`
          : ''}
      </Callout>
      {late ? (
        <Callout compact tone="warn" title="La pasada de anoche no ha corrido.">
          Tenía que haber corrido a las {preview.run_at}. Si mañana sigue igual, avisa a quien
          administra la aplicación.
        </Callout>
      ) : null}
    </div>
  );
}

function number(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}
